import { TextField } from "@mui/material";
import styled from "styled-components";
import {
  ChangeEvent,
  ChangeEventHandler,
  KeyboardEventHandler,
  KeyboardEvent,
  useEffect,
  FocusEventHandler,
  useRef,
} from "react";
import { ReactRefI } from "type/common/ref.type";

type Props = {
  label: string;
  defaultValue?: string;
  value?: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<Element>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: boolean;
  fieldRef?: ReactRefI<string>;
  focused?: boolean;
  id?: string;
  autoFocus?: boolean;
  inputLabelShrink?: boolean;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  minDate?: string;
  maxDate?: string;
  format?: string;
};

const DateInputField = ({
  label,
  defaultValue,
  value,
  onChange,
  onKeyDown,
  onBlur,
  error,
  fieldRef,
  focused,
  id,
  autoFocus,
  inputLabelShrink,
  disabled = false,
  required = false,
  placeholder,
  helperText,
  minDate,
  maxDate,
  format,
}: Props) => {
  useEffect(() => {
    if (fieldRef) {
      fieldRef.current = defaultValue ?? "";
    }
  }, [defaultValue, fieldRef]);

  const onChangeHandle = (
    ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (onChange) {
      onChange(ev);
    }
    if (fieldRef) {
      fieldRef.current = ev.target.value;
    }
  };

  const onKeyDownHandle = (ev: KeyboardEvent<Element>) => {
    if (onKeyDown) {
      onKeyDown(ev);
    }
  };

  const onBlurHandle = (
    ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (onBlur) {
      onBlur(ev);
    }
  };

  const inputLabelProps: { shrink?: boolean } = {};
  if (typeof inputLabelShrink !== "undefined") {
    inputLabelProps.shrink = inputLabelShrink;
  }

  // For date inputs, it's often better to always shrink the label
  // when there's a value or when focused
  const shouldShrinkLabel =
    inputLabelShrink !== undefined
      ? inputLabelShrink
      : !!(value || defaultValue || focused);

  if (inputLabelShrink === undefined && (value || defaultValue || focused)) {
    inputLabelProps.shrink = true;
  }

  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <DateStyleField
      onChange={onChangeHandle}
      onKeyDown={onKeyDownHandle}
      onBlur={onBlurHandle}
      label={label}
      defaultValue={defaultValue}
      value={value}
      error={error}
      type="date"
      name="dateField"
      id={id ?? undefined}
      focused={focused}
      autoFocus={typeof autoFocus !== "undefined" ? autoFocus : false}
      InputLabelProps={{ ...inputLabelProps, shrink: true }}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      helperText={helperText}
      inputProps={{
        min: minDate,
        max: maxDate,
      }}
      inputRef={inputRef}
      InputProps={{ onClick: handleInputClick }}
      // For date picker format customization if needed
      // InputProps={{
      //   inputProps: {
      //     pattern: format || "yyyy-MM-dd",
      //   },
      // }}
    />
  );
};

const DateStyleField = styled(TextField)`
  &&& .MuiFormLabel-root {
    color: currentColor;
  }

  &&& .MuiInputBase-root {
    border: ${(props) => (props?.error ? "red" : "auto")};
  }

  // Remove the default date picker icon if you want custom styling
  &&& input[type="date"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;

    &:hover {
      background: rgba(0, 0, 0, 0.04);
    }
  }

  // Style for when the field is disabled
  &&& .MuiInputBase-root.Mui-disabled {
    opacity: 0.7;
  }

  // Style for required field
  &&& .MuiFormLabel-root.Mui-required::after {
    content: " *";
    color: #d32f2f;
  }
`;

export default DateInputField;
